/**
 * FounderVetting — admin review queue for founder applications.
 *
 * Shows every assessment with AI council scorecard. Admin can take one of
 * four decisions and trigger the colour-coded email:
 *
 *    approve  → GREEN
 *    improve  → AMBER (improvement program)
 *    meeting  → AMBER (custom meeting note/url)
 *    reject   → RED (note required)
 *
 * Only system_admin + holding_admin can view / decide.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Rocket, ExternalLink, RefreshCw, ShieldCheck, CheckCircle2, XCircle,
  Sparkles, MessageCircle, Clock, FileText, Brain, Lock, Unlock, Download,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const VERDICT_TONE = {
  approve: { color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-500/10', border: 'border-emerald-300/60', label: 'AI · APPROVE' },
  improve: { color: 'text-amber-700 dark:text-amber-300',   bg: 'bg-amber-50 dark:bg-amber-500/10',     border: 'border-amber-300/60',   label: 'AI · IMPROVE' },
  reject:  { color: 'text-rose-700 dark:text-rose-300',     bg: 'bg-rose-50 dark:bg-rose-500/10',       border: 'border-rose-300/60',    label: 'AI · REJECT' },
};

const STATUS_TONE = {
  ai_running:     { color: 'text-sky-700 dark:text-sky-300',   bg: 'bg-sky-50 dark:bg-sky-500/10',     label: 'AI running' },
  awaiting_human: { color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-500/10', label: 'Awaiting human review' },
  approved:       { color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-500/10', label: 'Approved' },
  improve:        { color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-500/10', label: 'Improvement program' },
  meeting:        { color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-500/10', label: 'Meeting requested' },
  rejected:       { color: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-50 dark:bg-rose-500/10',   label: 'Rejected' },
};

function fmtDate(d) { return d ? new Date(d).toLocaleString() : '—'; }

function ScoreBar({ label, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const tone = v >= 75 ? 'bg-emerald-500' : v >= 55 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-3" data-testid={`score-${label}`}>
      <div className="w-44 text-[11px] uppercase tracking-[0.12em] text-slate-600 dark:text-white/65">{label.replace(/_/g, ' ')}</div>
      <div className="flex-1 h-2 bg-slate-100 dark:bg-white/[0.07] rounded-full overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${v}%` }}/>
      </div>
      <div className="w-10 text-right text-xs font-bold text-slate-900 dark:text-white">{v}</div>
    </div>
  );
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect + purity false-positives on the fetch + polling + timer effects. */
export default function FounderVetting() {
  const { user } = useAuth();
  const { assessmentId: routeAssessmentId } = useParams();
  const [tab, setTab] = useState('applications');
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortMode, setSortMode] = useState('newest');
  const [decision, setDecision] = useState('improve');
  const [note, setNote] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Batch-mode selection state — admin can multi-select awaiting_human
  // applications and apply one decision to all of them.
  const [selected, setSelected] = useState(() => new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());
  const selectAllGreen = (greenOnly = true) => {
    const next = new Set();
    for (const it of items) {
      if (it.status !== 'awaiting_human') continue;
      if (greenOnly) {
        if (it.council_overlay?.verdict === 'green') next.add(it.assessment_id);
      } else {
        next.add(it.assessment_id);
      }
    }
    setSelected(next);
  };

  const isAdmin = user && (user.role === 'system_admin' || user.role === 'holding_admin');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter === 'all' ? {} : { status: filter };
      if (sortMode && sortMode !== 'newest') params.sort = sortMode;
      const { data } = await api.get('/vetting/', { params });
      setItems(data);
      // Honour deep-link via URL param when present, else preserve current
      // selection, else fall back to the first item.
      if (routeAssessmentId) {
        const target = data.find((x) => x.assessment_id === routeAssessmentId);
        if (target) {
          setActive(target);
          return;
        }
      }
      if (active) {
        const next = data.find((x) => x.assessment_id === active.assessment_id);
        setActive(next || data[0] || null);
      } else if (data.length) {
        setActive(data[0]);
      }
    } catch {
      toast.error('Could not load assessments.');
    } finally { setLoading(false); }
  }, [filter, sortMode, routeAssessmentId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while there's an AI-running assessment in view
  useEffect(() => {
    if (!items.some((i) => i.status === 'ai_running')) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [items, load]);

  const rerun = async () => {
    if (!active) return;
    try {
      await api.post(`/vetting/${active.assessment_id}/rerun`);
      toast.success('AI council re-running.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-run failed');
    }
  };

  const submitDecision = async () => {
    if (!active) return;
    if ((decision === 'reject' || decision === 'meeting') && !note.trim()) {
      toast.error('Please add a note — required for this decision.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/vetting/${active.assessment_id}/decide`, {
        decision,
        note: note || undefined,
        meeting_url: decision === 'meeting' ? meetingUrl || undefined : undefined,
      });
      toast.success(`Decision saved — founder email queued.`);
      setNote(''); setMeetingUrl('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save decision');
    } finally { setBusy(false); }
  };

  // ── Batch decide with 30s "undo" window ────────────────────────────────
  // Gmail-style: we do NOT call the API immediately. A 30-second timer
  // counts down inside a sticky toast; if the admin clicks Undo before it
  // fires, no request is ever sent → no emails, no audit, no DB writes.
  // This is the only way "undo" can actually be honoured given that the
  // batch-decide endpoint emails each founder synchronously.
  const UNDO_WINDOW_MS = 30000;
  const [pendingBatch, setPendingBatch] = useState(null); // { decision, ids, note, expiresAt, toastId }

  const runBatch = (decision, note = '') => {
    if (!selected.size) return;
    if ((decision === 'reject' || decision === 'meeting') && !note.trim()) {
      toast.error('A note is required for this batch decision.');
      return;
    }
    // Refuse to queue a second batch on top of one already in-flight —
    // would otherwise be a UX nightmare to undo selectively.
    if (pendingBatch) {
      toast.error('A batch is already queued. Wait for it to send, or click Undo.');
      return;
    }
    const ids = Array.from(selected);
    const expiresAt = Date.now() + UNDO_WINDOW_MS;
    const toastId = `batch-${decision}-${expiresAt}`;
    setPendingBatch({ decision, ids, note, expiresAt, toastId });
    clearSelected();
    // The sticky toast is rendered separately (see <BatchUndoToast/> below)
    // because we want a live-updating countdown.
  };

  // When the undo window expires, actually fire the API call.
  useEffect(() => {
    if (!pendingBatch) return undefined;
    const remaining = pendingBatch.expiresAt - Date.now();
    const t = setTimeout(async () => {
      setBatchBusy(true);
      try {
        const { data } = await api.post('/vetting/batch-decide', {
          assessment_ids: pendingBatch.ids,
          decision: pendingBatch.decision,
          note: pendingBatch.note || undefined,
        });
        const msg = `${data.succeeded} succeeded · ${data.failed} failed`;
        if (data.failed === 0) toast.success(`Batch ${pendingBatch.decision}: ${msg}`);
        else toast.warning(`Batch ${pendingBatch.decision}: ${msg}`);
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Batch decision failed');
      } finally {
        setBatchBusy(false);
        setPendingBatch(null);
      }
    }, Math.max(0, remaining));
    return () => clearTimeout(t);
  }, [pendingBatch]);

  const undoPendingBatch = () => {
    if (!pendingBatch) return;
    setPendingBatch(null);
    toast.info(`Undid batch ${pendingBatch.decision} for ${pendingBatch.ids.length} application${pendingBatch.ids.length === 1 ? '' : 's'}.`);
  };

  const filtered = useMemo(() => items, [items]);

  const queueStats = useMemo(() => {
    let awaiting = 0;
    let green = 0;
    let amber = 0;
    let red = 0;
    for (const it of items) {
      if (it.status !== 'awaiting_human') continue;
      awaiting += 1;
      const v = it.council_overlay?.verdict;
      if (v === 'green') green += 1;
      else if (v === 'amber') amber += 1;
      else if (v === 'red') red += 1;
    }
    return { awaiting, green, amber, red };
  }, [items]);

  if (!isAdmin) {
    return (
      <div className="ve-container py-12">
        <div className="ve-card p-8 text-center">
          <ShieldCheck className="mx-auto text-[#5E1F8B] dark:text-[#8B3FC5]" size={28}/>
          <h2 className="display text-xl font-bold mt-3">Restricted</h2>
          <p className="text-sm text-slate-600 dark:text-white/65 mt-2">
            Only system administrators and holding admins can review founder applications.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ve-container py-6 md:py-10">
      {/* Sticky batch-undo bar — visible while pendingBatch is queued */}
      {pendingBatch && (
        <PendingBatchBar batch={pendingBatch} onUndo={undoPendingBatch}/>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="ve-eyebrow">Vetting</div>
          <h1 className="display text-2xl md:text-3xl mt-1 font-bold">
            {tab === 'applications' ? 'Founder Applications' : 'AI Q&A Review Queue'}
          </h1>
          <p className="text-sm text-slate-600 dark:text-white/65 mt-1">
            {tab === 'applications'
              ? 'AI-graded pitches awaiting your final decision. Each decision triggers an email to the founder.'
              : 'Founders blocked by the strict AI Q&A gate. Override with caution — every override is audit-logged.'}
          </p>
          {tab === 'applications' && queueStats.awaiting > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2"
                 data-testid="vetting-queue-stats">
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/60">
                {queueStats.awaiting} waiting
              </span>
              {queueStats.green > 0 && (
                <button
                  data-testid="vetting-green-cta"
                  onClick={() => { selectAllGreen(true); }}
                  className="px-2.5 py-1 rounded-full border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-[0.14em] hover:scale-105 transition">
                  <span className="text-emerald-700 dark:text-emerald-300">{queueStats.green}</span>
                  <span className="opacity-80"> green-overlay (≥ 70) · 1-click approve</span>
                </button>
              )}
              {queueStats.amber > 0 && (
                <span className="px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-[0.14em]">
                  {queueStats.amber} amber
                </span>
              )}
              {queueStats.red > 0 && (
                <span className="px-2.5 py-1 rounded-full border border-rose-300 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 text-[10px] font-bold uppercase tracking-[0.14em]">
                  {queueStats.red} red
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5 border-b border-slate-200 dark:border-white/10">
        {[
          { id: 'applications', label: 'Founder Applications', icon: Rocket },
          { id: 'qa_review',    label: 'AI Q&A Review Queue', icon: Brain },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`vetting-tab-${t.id}`}
              className={`relative px-4 py-2.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors
                ${active ? 'text-[#0A0A0A] dark:text-white' : 'text-slate-500 hover:text-slate-700 dark:text-white/55 dark:hover:text-white/80'}`}>
              <Icon size={13}/> {t.label}
              {active && <span className="absolute inset-x-2 -bottom-px h-[2px] bg-[#8B3FC5]"/>}
            </button>
          );
        })}
      </div>

      {tab === 'qa_review' && <QAReviewQueue />}

      {tab === 'applications' && (
      <>
      <div className="flex items-center justify-end gap-2 mb-4">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="vetting-filter"
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-white/[0.04]">
            <option value="all">All statuses</option>
            <option value="ai_running">AI running</option>
            <option value="awaiting_human">Awaiting review</option>
            <option value="approved">Approved</option>
            <option value="improve">Improvement program</option>
            <option value="meeting">Meeting requested</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} data-testid="vetting-sort"
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-white/[0.04]"
            title="Sort the queue">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first (SLA)</option>
            <option value="overlay_signal">Strongest council signal</option>
            <option value="ai_score">Highest AI score</option>
          </select>
          <button onClick={load} data-testid="vetting-refresh" className="ve-btn-secondary text-xs">
            <RefreshCw size={13}/> Refresh
          </button>
      </div>

      {/* Batch-action bar — appears the moment any row is selected */}
      {selected.size > 0 && (
        <div className="mb-4 ve-card p-3 flex flex-wrap items-center gap-2 border-[#8B3FC5]/40"
             data-testid="vetting-batch-bar">
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7A5A00] dark:text-[#8B3FC5] pr-2">
            {selected.size} selected
          </span>
          <button data-testid="vetting-batch-approve"
            disabled={batchBusy}
            onClick={() => runBatch('approve')}
            className="ve-btn-primary text-xs">
            <CheckCircle2 size={13}/> Approve all
          </button>
          <button data-testid="vetting-batch-improve"
            disabled={batchBusy}
            onClick={() => runBatch('improve', 'Group-routed to the Improvement Program.')}
            className="ve-btn-secondary text-xs">
            <Sparkles size={13}/> Improvement program
          </button>
          <button data-testid="vetting-batch-reject"
            disabled={batchBusy}
            onClick={() => {
              const reason = window.prompt('Rejection note (sent to all selected founders):');
              if (!reason || !reason.trim()) return;
              runBatch('reject', reason.trim());
            }}
            className="ve-btn-secondary text-xs">
            <XCircle size={13}/> Reject all
          </button>
          <button onClick={clearSelected}
            data-testid="vetting-batch-clear"
            className="ve-btn-ghost text-xs">
            Clear
          </button>
        </div>
      )}

      {/* "Quick select" shortcuts — visible when nothing's selected yet */}
      {selected.size === 0 && items.some((i) => i.status === 'awaiting_human') && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-slate-500 dark:text-white/55 uppercase tracking-[0.18em] font-bold">
            Quick select
          </span>
          <button data-testid="vetting-select-green-overlay"
            onClick={() => selectAllGreen(true)}
            className="px-2.5 py-1 rounded-full border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-[0.14em] hover:scale-105 transition">
            All green-overlay (≥ 70)
          </button>
          <button data-testid="vetting-select-all-awaiting"
            onClick={() => selectAllGreen(false)}
            className="px-2.5 py-1 rounded-full border border-slate-200 dark:border-white/15 text-slate-600 dark:text-white/65 text-[10px] font-bold uppercase tracking-[0.14em] hover:scale-105 transition">
            All awaiting review
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        {/* List */}
        <div className="ve-card overflow-hidden max-h-[78vh] overflow-y-auto" data-testid="vetting-list">
          {loading && <div className="p-6 text-sm text-slate-500 dark:text-white/55">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center">
              <Rocket size={32} className="mx-auto text-slate-300 dark:text-white/20 mb-3"/>
              <p className="text-sm text-slate-500 dark:text-white/55">No founder applications yet.</p>
            </div>
          )}
          {filtered.map((it) => {
            const v = VERDICT_TONE[it.ai_verdict] || {};
            const st = STATUS_TONE[it.status] || {};
            const isActive = active?.assessment_id === it.assessment_id;
            const isChecked = selected.has(it.assessment_id);
            const canBatch = it.status === 'awaiting_human';
            const co = it.council_overlay;
            const coTone = co ? ({
              green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-300/60',
              amber: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 border-amber-300/60',
              red:   'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 border-rose-300/60',
            }[co.verdict]) : '';
            return (
              <div key={it.assessment_id}
                data-testid={`vetting-row-${it.assessment_id}`}
                className={`group flex items-stretch border-b border-slate-100 dark:border-white/[0.06]
                  ${isActive ? 'bg-[#FFF8E5] dark:bg-[#8B3FC5]/[0.08]' : 'hover:bg-slate-50 dark:hover:bg-white/[0.02]'} transition-colors`}>
                {/* select checkbox — only for awaiting-human rows */}
                {canBatch ? (
                  <label
                    onClick={(e) => e.stopPropagation()}
                    className="pl-3 pr-1 flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelected(it.assessment_id)}
                      data-testid={`vetting-select-${it.assessment_id}`}
                      className="h-3.5 w-3.5 accent-[#8B3FC5] cursor-pointer"/>
                  </label>
                ) : <span className="w-7"/>}

                <button onClick={() => setActive(it)}
                  className="flex-1 text-left px-2 py-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-bold text-sm text-slate-900 dark:text-white truncate">{it.startup_name}</div>
                    <span className={`text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${st.bg} ${st.color} whitespace-nowrap`}>
                      {st.label}
                    </span>
                  </div>
                  <div className="mt-1 text-[12px] text-slate-500 dark:text-white/55 truncate">
                    {it.founder_name} · {it.industry}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {it.ai_verdict ? (
                        <span className={`text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded ${v.bg} ${v.color} border ${v.border} whitespace-nowrap`}>
                          {v.label} · {it.ai_score ?? '—'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-white/45">
                          <Clock size={11}/> grading…
                        </span>
                      )}
                      {co && (
                        <span
                          data-testid={`vetting-row-overlay-${it.assessment_id}`}
                          title={`Council overlay · ai ${co.components.ai_score} × 60% + lead ${co.components.leadership_index} × 25% + risk-align ${co.components.risk_alignment} × 15%`}
                          className={`text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded border ${coTone} whitespace-nowrap`}>
                          {co.verdict} · {Math.round(co.decision_signal)}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400 dark:text-white/35 whitespace-nowrap">
                      {fmtDate(it.created_at).slice(0, 16)}
                    </span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Detail */}
        {!active ? (
          <div className="ve-card p-8 text-center text-sm text-slate-500 dark:text-white/55">
            Select an application to view the AI scorecard.
          </div>
        ) : (
          <div className="ve-card p-6 md:p-8" data-testid="vetting-detail">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="ve-eyebrow">Application</div>
                <h2 className="display text-2xl font-bold mt-1">{active.startup_name}</h2>
                <div className="mt-1 text-sm text-slate-600 dark:text-white/65">
                  by <b className="text-slate-900 dark:text-white">{active.founder_name}</b>
                  <span className="opacity-60"> · {active.founder_email}</span>
                  <span className="opacity-60"> · {active.industry}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {active.pitch_deck_url && (
                  <a href={active.pitch_deck_url} target="_blank" rel="noreferrer"
                    data-testid="vetting-open-deck"
                    className="ve-btn-secondary text-xs">
                    <ExternalLink size={13}/> Open deck
                  </a>
                )}
                <button onClick={rerun} data-testid="vetting-rerun" className="ve-btn-secondary text-xs">
                  <RefreshCw size={13}/> Re-run AI
                </button>
                {active.human_decision === 'approve' && (
                  <>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/vetting/${active.assessment_id}/memo.pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid="vetting-download-memo"
                      className="ve-btn-primary text-xs">
                      <Download size={13}/> Memo PDF
                    </a>
                    <button
                      type="button"
                      data-testid="vetting-invalidate-memo"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/vetting/${active.assessment_id}/memo/invalidate`);
                          toast.success(
                            data.had_cache
                              ? 'Memo cache cleared — next download will re-render.'
                              : 'No cached memo to clear.',
                          );
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Could not invalidate memo');
                        }
                      }}
                      title="Clear cached memo so the next download re-renders"
                      className="ve-btn-ghost text-xs">
                      <RefreshCw size={13}/> Re-render
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Pitch summary */}
            <div className="mt-6">
              <div className="ve-eyebrow flex items-center gap-1.5"><FileText size={11}/>Pitch summary</div>
              <p className="mt-2 text-sm whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-white/80">
                {active.pitch_summary}
              </p>
            </div>

            {/* AI scorecard */}
            <div className="mt-7 grid md:grid-cols-[1.1fr_1fr] gap-6">
              <div>
                <div className="ve-eyebrow flex items-center gap-1.5"><Sparkles size={11}/>AI Council Scorecard</div>
                {active.status === 'ai_running' ? (
                  <div className="mt-3 ve-card !border-dashed p-5 text-sm text-slate-500 dark:text-white/55">
                    AI council deliberating…
                  </div>
                ) : (
                  <div className="mt-3 space-y-2.5">
                    {active.ai_scores
                      ? Object.entries(active.ai_scores).map(([k, v]) => (
                        <ScoreBar key={k} label={k} value={v}/>
                      )) : (
                        <div className="text-sm text-slate-500 dark:text-white/55">No scorecard yet.</div>
                      )}
                  </div>
                )}
                {active.ai_score != null && (
                  <div className="mt-4 flex items-center gap-3">
                    <span className="ve-eyebrow">Overall</span>
                    <span className="display text-3xl font-bold text-[#7A5A00] dark:text-[#8B3FC5]">{active.ai_score}/100</span>
                    {active.ai_verdict && (() => {
                      const v = VERDICT_TONE[active.ai_verdict] || {};
                      return <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-full border ${v.bg} ${v.color} ${v.border}`}>{v.label}</span>;
                    })()}
                  </div>
                )}
                {active.ai_rationale && (
                  <p className="mt-3 text-xs italic text-slate-500 dark:text-white/60 leading-relaxed">
                    {active.ai_rationale}
                  </p>
                )}
              </div>
              <div>
                <div className="ve-eyebrow">Strengths</div>
                <ul className="mt-2 space-y-1.5">
                  {(active.ai_strengths || []).map((s, i) => (
                    <li key={`str-${i}-${(s || '').slice(0, 32)}`} className="text-xs text-slate-700 dark:text-white/80 flex gap-2">
                      <CheckCircle2 size={12} className="text-emerald-600 mt-0.5 flex-shrink-0"/>
                      <span>{s}</span>
                    </li>
                  ))}
                  {!(active.ai_strengths || []).length && <li className="text-xs text-slate-500 dark:text-white/45">—</li>}
                </ul>
                <div className="ve-eyebrow mt-4">Concerns</div>
                <ul className="mt-2 space-y-1.5">
                  {(active.ai_concerns || []).map((s, i) => (
                    <li key={i} className="text-xs text-slate-700 dark:text-white/80 flex gap-2">
                      <XCircle size={12} className="text-rose-500 mt-0.5 flex-shrink-0"/>
                      <span>{s}</span>
                    </li>
                  ))}
                  {!(active.ai_concerns || []).length && <li className="text-xs text-slate-500 dark:text-white/45">—</li>}
                </ul>
              </div>
            </div>

            {/* Psychometric profile (Big-5 / risk / leadership) */}
            <PsychometricPanel assessmentId={active.assessment_id}/>

            {/* Decision */}
            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-white/[0.08]">
              <div className="ve-eyebrow">Human decision</div>

              {active.human_decision ? (
                <div className="mt-3 p-4 ve-card !border-emerald-300/40 bg-emerald-50/50 dark:bg-emerald-500/[0.06]">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-800 dark:text-emerald-300">
                    Decision · {active.human_decision}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-white/65 mt-1">
                    {active.human_note || '—'}
                  </p>
                  <p className="text-[10.5px] text-slate-500 dark:text-white/45 mt-2">
                    {fmtDate(active.human_decided_at)}
                  </p>
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { v: 'approve', label: 'Approve', icon: CheckCircle2, tone: 'emerald' },
                      { v: 'improve', label: 'Improvement program', icon: Sparkles, tone: 'amber' },
                      { v: 'meeting', label: 'Meeting requested', icon: MessageCircle, tone: 'amber' },
                      { v: 'reject',  label: 'Reject',  icon: XCircle, tone: 'rose' },
                    ].map((b) => {
                      const Icon = b.icon;
                      const selected = decision === b.v;
                      const toneCls =
                        b.tone === 'emerald'
                          ? (selected ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'border-slate-200 dark:border-white/15')
                          : b.tone === 'amber'
                            ? (selected ? 'border-amber-500 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-slate-200 dark:border-white/15')
                            : (selected ? 'border-rose-500 bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300' : 'border-slate-200 dark:border-white/15');
                      return (
                        <button key={b.v} type="button" onClick={() => setDecision(b.v)}
                          data-testid={`vetting-decision-${b.v}`}
                          className={`flex flex-col items-start gap-1 p-3 rounded-lg border-2 transition-all text-left ${toneCls}`}>
                          <Icon size={16}/>
                          <span className="text-[10px] uppercase tracking-[0.16em] font-bold">{b.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <textarea
                    rows={3}
                    placeholder={
                      decision === 'reject'  ? 'Why are we declining? (required, will appear in the founder email)'
                      : decision === 'meeting' ? 'What should we discuss? (required, included in the founder email)'
                      : 'Optional note included in the founder email…'
                    }
                    value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid="vetting-decision-note"
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>

                  {decision === 'meeting' && (
                    <input type="url" placeholder="Optional · Calendly / Google Meet booking link"
                      value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)}
                      data-testid="vetting-meeting-url"
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/[0.04]"/>
                  )}

                  <button onClick={submitDecision} disabled={busy || active.status === 'ai_running'}
                    data-testid="vetting-submit-decision"
                    className="ve-btn-primary text-sm">
                    {busy ? 'Saving…' : `Confirm & email founder`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

// ============================================================================
// PENDING BATCH BAR — sticky countdown with an Undo button
// ============================================================================
function PendingBatchBar({ batch, onUndo }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, []);
  const remainingMs = Math.max(0, batch.expiresAt - now);
  const secs = Math.ceil(remainingMs / 1000);
  const pct = Math.max(0, Math.min(100, (remainingMs / 30000) * 100));
  const decisionLabel = {
    approve: 'approve',
    improve: 'route to Improvement Program',
    reject:  'reject',
    meeting: 'request meeting with',
  }[batch.decision] || batch.decision;

  return (
    <div
      data-testid="vetting-batch-pending"
      className="sticky top-2 z-30 mb-4 ve-card !border-[#8B3FC5] bg-[#0A0A0A]/95 text-white shadow-2xl overflow-hidden">
      <div className="flex items-center gap-4 px-5 py-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="display text-2xl font-bold text-[#8B3FC5] tabular-nums">
            {secs}s
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#8B3FC5]">
              Batch send in progress
            </div>
            <div className="text-xs text-white/80 mt-0.5">
              About to <b>{decisionLabel}</b>{' '}
              <b>{batch.ids.length}</b> application{batch.ids.length === 1 ? '' : 's'}.
              No emails have been sent yet.
            </div>
          </div>
        </div>
        <button
          onClick={onUndo}
          data-testid="vetting-batch-undo"
          className="ve-btn-primary text-xs whitespace-nowrap">
          Undo
        </button>
      </div>
      <div className="h-1 bg-white/[0.08]">
        <div className="h-full bg-[#8B3FC5] transition-all duration-100"
             style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}


// ============================================================================
// PSYCHOMETRIC PANEL — Big-5 / risk-tolerance / leadership for an assessment
// ============================================================================
function PsychometricPanel({ assessmentId }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null); setData(null);
    (async () => {
      try {
        const { data } = await api.get(`/vetting/${assessmentId}/psychometric`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 404
          ? 'Founder has not completed the psychometric self-report yet.'
          : (e?.response?.data?.detail || 'Could not load psychometric'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentId]);

  return (
    <div className="mt-7 pt-6 border-t border-slate-200 dark:border-white/[0.08]"
         data-testid="psychometric-panel">
      <div className="ve-eyebrow flex items-center gap-1.5">
        <Brain size={11}/>Founder Psychometric
      </div>

      {loading && <div className="mt-3 text-sm text-slate-500 dark:text-white/55">Loading…</div>}

      {!loading && err && (
        <div className="mt-3 ve-card !border-dashed p-4 text-sm text-slate-500 dark:text-white/55">
          {err}
        </div>
      )}

      {!loading && data && (
        <>
        {data.council_overlay && (() => {
          const co = data.council_overlay;
          const toneCls = {
            green: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300/60 text-emerald-700 dark:text-emerald-300',
            amber: 'bg-amber-50 dark:bg-amber-500/10 border-amber-300/60 text-amber-700 dark:text-amber-300',
            red:   'bg-rose-50 dark:bg-rose-500/10 border-rose-300/60 text-rose-700 dark:text-rose-300',
          }[co.verdict];
          return (
            <div className={`mt-3 mb-4 rounded-lg border p-4 flex items-start gap-4 ${toneCls}`}
                 data-testid="council-overlay">
              <div className="display text-3xl font-bold leading-none">
                {Math.round(co.decision_signal)}<span className="text-sm opacity-50">/100</span>
              </div>
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold">
                  Council overlay · {co.verdict.toUpperCase()}
                </div>
                <div className="text-[11px] mt-1 opacity-80 leading-snug">
                  ai {co.components.ai_score} × 60 % &nbsp;·&nbsp;
                  leadership {co.components.leadership_index} × 25 % &nbsp;·&nbsp;
                  risk-align {co.components.risk_alignment} × 15 %
                  &nbsp;<span className="opacity-60">(risk raw {co.components.risk_raw})</span>
                </div>
              </div>
            </div>
          );
        })()}
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-6">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/55 mb-2">
              Big Five (OCEAN)
            </div>
            <div className="space-y-2">
              {['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism']
                .map((k) => (
                  <ScoreBar key={k} label={k} value={Math.round(data.big_five?.[k] ?? 0)}/>
                ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="ve-card p-3">
                <div className="ve-eyebrow">Leadership index</div>
                <div className="display text-2xl font-bold mt-1 text-[#7A5A00] dark:text-[#8B3FC5]">
                  {Math.round(data.leadership_index || 0)}<span className="text-sm opacity-50">/100</span>
                </div>
              </div>
              <div className="ve-card p-3">
                <div className="ve-eyebrow">Risk appetite</div>
                <div className="display text-2xl font-bold mt-1 text-[#7A5A00] dark:text-[#8B3FC5]">
                  {Math.round(data.risk_appetite || 0)}<span className="text-sm opacity-50">/100</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/55 mb-2">
              Profile summary
            </div>
            <p className="text-xs leading-relaxed text-slate-700 dark:text-white/80 italic">
              {data.summary || '—'}
            </p>

            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/55 mt-4 mb-2">
              Flags
            </div>
            <ul className="space-y-1.5">
              {(data.flags || []).map((f, i) => (
                <li key={`flag-${i}-${(f || '').slice(0, 32)}`} className="text-xs text-slate-700 dark:text-white/80 flex gap-2">
                  <XCircle size={12} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"/>
                  <span>{f}</span>
                </li>
              ))}
              {!(data.flags || []).length && (
                <li className="text-xs text-slate-500 dark:text-white/45">No flags raised.</li>
              )}
            </ul>
          </div>
        </div>
        </>
      )}
    </div>
  );
}


// ============================================================================
// QA REVIEW QUEUE — strict AI Q&A gate overrides for admins
// ============================================================================
function QAReviewQueue() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcePass, setForcePass] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/startups/qa/review-queue');
      setRows(data);
      if (active) {
        const next = data.find((x) => x.startup_id === active.startup_id);
        setActive(next || data[0] || null);
      } else if (data.length) {
        setActive(data[0]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load review queue.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!active) return;
    if (!reason.trim()) {
      toast.error('A justification is required for any override.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/startups/qa/override/${active.startup_id}`, {
        pass: forcePass,
        reason,
        unlock_attempts: true,
      });
      toast.success(`AI Q&A ${forcePass ? 'override-PASS' : 'override-FAIL'} recorded.`);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save override');
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-6" data-testid="qa-review-queue">
      <div className="ve-card overflow-hidden max-h-[78vh] overflow-y-auto" data-testid="qa-review-list">
        {loading && <div className="p-6 text-sm text-slate-500 dark:text-white/55">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center">
            <Brain size={32} className="mx-auto text-slate-300 dark:text-white/20 mb-3"/>
            <p className="text-sm text-slate-500 dark:text-white/55">No blocked AI Q&amp;A sessions.</p>
          </div>
        )}
        {rows.map((it) => {
          const isActive = active?.startup_id === it.startup_id;
          const locked = it.qa_locked;
          return (
            <button key={it.startup_id} onClick={() => setActive(it)}
              data-testid={`qa-review-row-${it.startup_id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-white/[0.06]
                ${isActive ? 'bg-[#FFF8E5] dark:bg-[#8B3FC5]/[0.08]' : 'hover:bg-slate-50 dark:hover:bg-white/[0.02]'} transition-colors`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-sm text-slate-900 dark:text-white truncate">{it.name || '—'}</div>
                {locked ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
                    <Lock size={9}/> Locked
                  </span>
                ) : (
                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                    Failed
                  </span>
                )}
              </div>
              <div className="mt-1 text-[12px] text-slate-500 dark:text-white/55 truncate">
                {it.founder_name || '—'} · {it.industry || '—'}
              </div>
              <div className="mt-1.5 text-[11px] text-slate-500 dark:text-white/55">
                attempts <b className="text-slate-700 dark:text-white">{it.qa_attempts}</b>
                {' · '}left <b className="text-slate-700 dark:text-white">{it.qa_attempts_left}</b>
                {it.qa_score != null && <> · score <b className="text-slate-700 dark:text-white">{Math.round(it.qa_score)}</b></>}
              </div>
            </button>
          );
        })}
      </div>

      {!active ? (
        <div className="ve-card p-8 text-center text-sm text-slate-500 dark:text-white/55">
          Select a startup to override its AI Q&amp;A gate.
        </div>
      ) : (
        <div className="ve-card p-6 md:p-8" data-testid="qa-review-detail">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="ve-eyebrow">Startup</div>
              <h2 className="display text-2xl font-bold mt-1">{active.name}</h2>
              <div className="mt-1 text-sm text-slate-600 dark:text-white/65">
                <b className="text-slate-900 dark:text-white">{active.founder_name}</b>
                <span className="opacity-60"> · {active.founder_email}</span>
                <span className="opacity-60"> · {active.industry}</span>
              </div>
            </div>
            <button onClick={load} className="ve-btn-secondary text-xs"><RefreshCw size={13}/> Refresh</button>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="ve-card !border-rose-200 bg-rose-50/40 p-4 dark:bg-rose-500/[0.04] dark:!border-rose-500/30">
              <div className="ve-eyebrow text-rose-700 dark:text-rose-300">Gate state</div>
              <div className="text-2xl font-bold display text-slate-900 dark:text-white mt-1">
                {active.qa_verdict?.toUpperCase() || '—'} · {active.qa_score != null ? Math.round(active.qa_score) : '—'}/100
              </div>
              <p className="mt-2 text-xs text-slate-600 dark:text-white/70 leading-relaxed">
                Attempts used: <b>{active.qa_attempts}</b> / max ({active.qa_attempts + active.qa_attempts_left}) ·
                {active.qa_locked ? ' fully locked' : ` ${active.qa_attempts_left} retry available`}
              </p>
            </div>
            <div className="ve-card p-4">
              <div className="ve-eyebrow">Latest session</div>
              <p className="text-xs text-slate-600 dark:text-white/70 mt-1.5 leading-relaxed">
                final_score: <b>{active.last_session?.final_score ?? '—'}</b><br/>
                final_verdict: <b>{active.last_session?.final_verdict ?? '—'}</b><br/>
                completed: <b>{active.last_session?.completed ? 'yes' : 'no'}</b>
              </p>
            </div>
          </div>

          <div className="mt-7 pt-6 border-t border-slate-200 dark:border-white/[0.08]">
            <div className="ve-eyebrow">Override action</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { v: true,  label: 'Force PASS · unlock journey', icon: Unlock, tone: 'emerald' },
                { v: false, label: 'Force FAIL · lock journey',   icon: Lock, tone: 'rose' },
              ].map((b) => {
                const Icon = b.icon;
                const selected = forcePass === b.v;
                const tone =
                  b.tone === 'emerald'
                    ? (selected ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'border-slate-200 dark:border-white/15')
                    : (selected ? 'border-rose-500 bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300' : 'border-slate-200 dark:border-white/15');
                return (
                  <button key={String(b.v)} type="button" onClick={() => setForcePass(b.v)}
                    data-testid={`qa-override-${b.v ? 'pass' : 'fail'}`}
                    className={`flex flex-col items-start gap-1 p-3 rounded-lg border-2 transition-all text-left ${tone}`}>
                    <Icon size={16}/>
                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold">{b.label}</span>
                  </button>
                );
              })}
            </div>

            <textarea
              rows={3}
              placeholder="Justification (required, audit-logged)…"
              value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="qa-override-reason"
              className="mt-3 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/20 focus:border-[#8B3FC5] dark:border-white/15 dark:bg-white/[0.04]"/>

            <button onClick={submit} disabled={busy}
              data-testid="qa-override-submit"
              className="ve-btn-primary text-sm mt-3">
              {busy ? 'Saving…' : 'Apply override'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
